"use client";

import * as React from "react";
import { Member, useRolesAndConfig } from "../api/endpoint.js";
import { DeleteUserDialog } from "./delete-user-dialog.js";
import { EditUserRoleDialog } from "./edit-user-role-dialog.js";
import { DropdownMenu } from "./elements.js";
import { ResendInviteDialog } from "./resend-invite-dialog.js";
import { RevokeInviteDialog } from "./revoke-invite-dialog.js";
import { Translation } from "./i18n/translation.js";
import { useTranslation } from "./i18n/use-translation.js";

interface UserActionsDropdownProps {
  user: Member;
  children: React.ReactNode;
}

type UserActionDialog =
  | "revoke-membership"
  | "revoke-invite"
  | "resend-invite"
  | "edit-role";

export const UserActionsDropdown = ({
  user,
  children,
}: UserActionsDropdownProps) => {
  const translate = useTranslation();
  const rolesAndConfigQuery = useRolesAndConfig({
    query: {
      initialData: { roles: [], multipleRolesEnabled: false },
    },
  });
  const { roles, multipleRolesEnabled: isMultipleRolesEnabled } =
    rolesAndConfigQuery.data;
  const [openDialog, setOpenDialog] = React.useState<UserActionDialog | null>(
    null,
  );

  /**
   * Assigns a key for each dialog based on its open state to ensure its
   * internal state is cleared when it is closed.
   */
  function getDialogKey(dialog: UserActionDialog) {
    return `${dialog}-${openDialog === dialog}-${user.id}`;
  }

  const { actions, items } = React.useMemo(() => {
    const actions = new Set(user.actions);
    const items: React.ReactElement[] = [];
    if (actions.has("edit-role")) {
      items.push(
        <DropdownMenu.Item
          key="edit-role"
          onSelect={() => setOpenDialog("edit-role")}
          disabled={
            rolesAndConfigQuery.isLoading ||
            (rolesAndConfigQuery.isSuccess && roles.length <= 1)
          }
          title={
            rolesAndConfigQuery.isSuccess && roles.length <= 1
              ? translate({
                  defaultMessage:
                    "You cannot update the role for this user as there is only one role available.",
                  id: "rzvafM",
                  description:
                    "Tooltip explaining why role editing is disabled",
                })
              : undefined
          }
        >
          {isMultipleRolesEnabled ? (
            <Translation
              defaultMessage="Edit roles"
              id="yjbD6m"
              description="Menu option to edit user roles (plural)"
            />
          ) : (
            <Translation
              defaultMessage="Edit role"
              id="NJqcnW"
              description="Menu option to edit user role (singular)"
            />
          )}
        </DropdownMenu.Item>,
      );
    }
    if (actions.has("resend-invite")) {
      items.push(
        <DropdownMenu.Item
          key="resend-invite"
          onSelect={() => setOpenDialog("resend-invite")}
        >
          <Translation
            defaultMessage="Resend invitation"
            id="1fzoWe"
            description="Menu option to resend user invitation"
          />
        </DropdownMenu.Item>,
      );
    }
    if (actions.has("revoke-invite")) {
      items.push(
        <DropdownMenu.Item
          variant="destructive"
          key="revoke-invite"
          onSelect={() => setOpenDialog("revoke-invite")}
        >
          <Translation
            defaultMessage="Revoke invitation"
            id="0Ie4dM"
            description="Menu option to revoke user invitation"
          />
        </DropdownMenu.Item>,
      );
    }
    if (actions.has("revoke-membership")) {
      items.push(
        <DropdownMenu.Item
          variant="destructive"
          key="revoke-membership"
          onSelect={() => setOpenDialog("revoke-membership")}
        >
          <Translation
            defaultMessage="Remove user"
            id="qGSR1P"
            description="Menu option to remove user from organization"
          />
        </DropdownMenu.Item>,
      );
    }
    return {
      actions,
      items,
    };
  }, [
    rolesAndConfigQuery,
    user.actions,
    isMultipleRolesEnabled,
    roles,
    translate,
  ]);

  if (user.isLoggedInUser || items.length === 0) {
    return null;
  }

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>{children}</DropdownMenu.Trigger>
        <DropdownMenu.Content size="2" align="end">
          {items}
        </DropdownMenu.Content>
      </DropdownMenu.Root>

      {actions.has("edit-role") && (
        <EditUserRoleDialog
          key={getDialogKey("edit-role")}
          user={user}
          open={openDialog === "edit-role"}
          onOpenChange={(open) => !open && setOpenDialog(null)}
        />
      )}

      {actions.has("revoke-membership") && (
        <DeleteUserDialog
          key={getDialogKey("revoke-membership")}
          user={user}
          open={openDialog === "revoke-membership"}
          onOpenChange={(open) => !open && setOpenDialog(null)}
        />
      )}

      {actions.has("revoke-invite") && (
        <RevokeInviteDialog
          key={getDialogKey("revoke-invite")}
          user={user}
          open={openDialog === "revoke-invite"}
          onOpenChange={(open) => !open && setOpenDialog(null)}
        />
      )}

      {actions.has("resend-invite") && (
        <ResendInviteDialog
          key={getDialogKey("resend-invite")}
          user={user}
          open={openDialog === "resend-invite"}
          onOpenChange={(open) => !open && setOpenDialog(null)}
        />
      )}
    </>
  );
};
